// A time simulation's run: items put in where work starts, passed from step to step, and what that shows. The analysis
// page's simulation draws a run on its graph; a run can also be made straight from analysis data, without a graph, so
// a project's future state can be simulated against its current state
import { v4 } from "uuid"
import { AnalysisEdge, AnalysisNode } from "../common/analysis-model"
import { ACTION_FREQUENCY_TYPE, ACTION_TYPE, NODE_SUPERTYPE, WORK_TYPE } from "../common/linqset-base"
import { SimulatedOutput, SimulatedStep, SimulationSnapshot, WORKING_HOURS_PER_YEAR, WORKING_SECONDS_PER_DAY } from "./simulation-report"
import { Item, TemporalNode } from "./temporal-model"

export const SECONDS_PER_YEAR = WORKING_HOURS_PER_YEAR*3600

// what a run needs to know about a node, as analysis data gives it
export type SimulatedNodeInfo = Pick<AnalysisNode, '_key' | 'name' | 'modelKey' | 'superType'> & Partial<Pick<AnalysisNode, 'roles' | 'actionType' | 'items' | 'workKind'>>

// how an action is simulated
export interface StepSettings {
    durationSeconds: number
    people: number                  // how many can work on it at once (0 for no limit)
    processType: ACTION_FREQUENCY_TYPE
    secondsPerTickEach: number      // how much of each tick each of them works on it
    waits?: boolean                 // items wait there, with nobody working on them (see WORK_TYPE)
}

// a wait's duration on the simulation's clock, which runs through working days: each of its days is one of the
// simulation's days, and the hours and minutes it has besides are as they are. Durations hold a day as 24 hours
export const waitOnWorkingClock = (seconds: number) => Math.floor(seconds/86400)*WORKING_SECONDS_PER_DAY + seconds%86400

// an action's settings from its duration, people, work and capacity (in hours a year)
export const stepSettings = (secondsPerTick: number, durationSeconds: number, people: number, hours: number, capacity: number, processType: ACTION_FREQUENCY_TYPE, waits = false): StepSettings => {
    // nobody works where items wait, so any number wait at once, for as long as the wait
    if (waits) return { durationSeconds: waitOnWorkingClock(durationSeconds), people: 0, processType, secondsPerTickEach: secondsPerTick, waits }
    // each person works on the step for the whole of a tick, unless their role has more work than capacity, when they're
    // slowed to the share of the work it can cover. Without hours or capacity there's nothing to slow it by
    const secondsPerTickEach = capacity > 0 && hours > capacity ? secondsPerTick*capacity/hours : secondsPerTick
    // at least as many people as its capacity takes: a role whose capacity isn't set is counted as one person, though
    // its capacity is taken to cover its work
    if (people > 0 && capacity > 0) people = Math.max(people, Math.ceil(capacity/WORKING_HOURS_PER_YEAR))
    return { durationSeconds, people, processType, secondsPerTickEach }
}

export const createTemporalNode = (info: SimulatedNodeInfo, settings?: StepSettings) => {
    const node = settings
        ? new TemporalNode(info._key, info.name, settings.durationSeconds, settings.people, settings.processType, settings.secondsPerTickEach)
        : new TemporalNode(info._key, info.name, 0, 0, ACTION_FREQUENCY_TYPE.SUM, 0)
    node.waits = !!settings?.waits
    return node
}

export interface SimulationSource {
    node: TemporalNode
    itemsPerYear: number
    proportionLeft: number
}

export class SimulationRun {
    tickNumber = 1
    totalReceived = 0
    readonly ticksPerYear: number
    readonly ticksPerDay: number
    private startingTick: number

    // sources: where items are put in. nodes: every node simulated, with what's known about it. outputs: where work ends
    constructor(readonly secondsPerTick: number, readonly sources: SimulationSource[], readonly nodes: { node: TemporalNode, info: SimulatedNodeInfo }[],
        readonly outputs: { node: TemporalNode, info: SimulatedNodeInfo }[], readonly predecessors: Map<string, string[]>) {
        this.ticksPerYear = Math.round(SECONDS_PER_YEAR/secondsPerTick)
        this.ticksPerDay = Math.max(1, Math.round(WORKING_SECONDS_PER_DAY/secondsPerTick))
        // the first items arrive as soon as the busiest source has one
        this.startingTick = Math.min(...sources.map(s => Math.round(1/(s.itemsPerYear/this.ticksPerYear))), Infinity)
    }

    get finished() {
        return this.tickNumber >= this.ticksPerYear
    }

    // one tick: items put in, worked on and passed on
    tick() {
        this.sources.forEach(s => {
            let num = s.itemsPerYear/this.ticksPerYear + (s.proportionLeft || 0)
            if (this.tickNumber === 1 && this.startingTick > 1 && isFinite(this.startingTick)) {
                num = num*this.startingTick
                this.tickNumber = this.startingTick
            }
            const itemNum = Math.trunc(num)
            s.proportionLeft = num - itemNum
            const items: Item[] = []
            for (let i = 0; i < itemNum; i++) items.push(new Item(v4(), s.node._key, this.tickNumber))
            s.node.receiveItems(this.tickNumber, items)
            this.totalReceived += itemNum
        })

        // the queues at the start of each day show which are growing
        if (this.tickNumber % this.ticksPerDay === 0) {
            this.nodes.forEach(({ node }) => {
                node.dayMarks.push({ tick: this.tickNumber, queue: node.queue })
                if (node.dayMarks.length > 2) node.dayMarks.shift()
            })
        }
    }

    // ticks on through the working days given, a few hundred ticks at a time, letting the page carry on in between
    async runFor(days: number, progress?: (share: number) => void) {
        const end = Math.min(this.ticksPerYear, this.tickNumber + Math.round(days*this.ticksPerDay))
        const start = this.tickNumber
        while (this.tickNumber < end) {
            const stop = Math.min(end, this.tickNumber + 200)
            while (this.tickNumber < stop) {
                this.tick()
                this.tickNumber++
            }
            progress?.((this.tickNumber - start)/Math.max(1, end - start))
            await new Promise(resolve => setTimeout(resolve))
        }
    }

    // the run as it stands, for its results
    snapshot(): SimulationSnapshot {
        const steps: SimulatedStep[] = this.nodes.filter(({ info }) => info.superType === NODE_SUPERTYPE.ACTION).map(({ node, info }) => ({
            key: node._key, name: info.name || 'Unnamed step', modelKey: info.modelKey, roleKeys: (info.roles || []).map(r => r._key),
            manual: info.actionType === ACTION_TYPE.MANUAL, queue: node.queue, onhold: node.onhold.length, maxQueue: node.maxQueue,
            entered: node.entered, completed: node.completed, durationSeconds: node.durationSecondsPerItem, people: node.numProcessors,
            secondsPerTickEach: node.processingTimePerTick, queueMark: node.dayMarks[0], waits: node.waits,
            joinInputs: node.type === ACTION_FREQUENCY_TYPE.MINIMUM && node.inputs.length > 1 ? node.inputCounts() : undefined,
        }))
        const outputs: SimulatedOutput[] = this.outputs.map(({ node, info }) => {
            const finished = node.finished
            const average = (value: (i: Item) => number) => finished.length ? finished.reduce((sum, i) => sum + value(i), 0)/finished.length : 0
            return {
                key: info._key, name: info.name || 'Unnamed output', modelKey: info.modelKey, produced: finished.length, itemsPerYear: info.items || 0,
                leadSeconds: average(i => (i.endTick - i.startTick)*this.secondsPerTick), workSeconds: average(i => i.noQueueTime),
                waitStepSeconds: average(i => i.waitTime),
            }
        })
        return {
            tick: this.tickNumber, ticksPerDay: this.ticksPerDay, secondsPerTick: this.secondsPerTick,
            yearFraction: Math.min(1, Math.max(0, this.tickNumber - 1)/this.ticksPerYear), received: this.totalReceived,
            finished: this.finished, steps, outputs, predecessors: this.predecessors,
        }
    }

    // a run of the analysis data for the models given, as the analysis page draws them: one node for each key, and the
    // connections between them. Only what items can reach is simulated
    static fromAnalysis(data: { nodes: AnalysisNode[], edges: AnalysisEdge[] }, modelKeys: Set<string>, secondsPerTick: number): SimulationRun {
        const infos = new Map(data.nodes.filter(n => modelKeys.has(n.modelKey)).map(n => [n._key, n]))
        const edges = data.edges.filter(e => modelKeys.has(e.modelKey) && infos.has(e.from) && infos.has(e.to))
        const outgoing = new Map<string, AnalysisEdge[]>()
        edges.forEach(e => (outgoing.get(e.from) || outgoing.set(e.from, []).get(e.from)!).push(e))

        const nodes = new Map<string, TemporalNode>()
        const outputs: { node: TemporalNode, info: SimulatedNodeInfo }[] = []
        const add = (key: string): TemporalNode => {
            const existing = nodes.get(key)
            if (existing) return existing
            const info = infos.get(key)!
            const node = createTemporalNode(info, info.superType === NODE_SUPERTYPE.ACTION
                ? stepSettings(secondsPerTick, info.durationSeconds || 0, info.numPeople || 0, info.hours || 0, info.capacity || 0, info.processType, info.workKind === WORK_TYPE.WAITING)
                : undefined)
            nodes.set(key, node)
            const next = outgoing.get(key) || []
            if (!next.length) outputs.push({ node, info })
            onPath.add(key)
            // a connection back to a step on the way here (rework, say) closes a loop, which the insights don't go round
            // either: items copied around one would multiply without end
            next.filter(e => !onPath.has(e.to)).forEach(e => node.addNext(add(e.to), e.percentage))
            onPath.delete(key)
            return node
        }
        const onPath = new Set<string>()

        // items come in where the work starts, and at actions that set how often they happen, as the insights do
        const sources = [...infos.values()].filter(n => n.isStarting || n.setsFrequency).map(info => {
            const node = add(info._key)
            if (info.setsFrequency && !info.isStarting) node.setsOwnVolume = true
            return { node, itemsPerYear: info.items || 0, proportionLeft: 0 }
        })

        const predecessors = new Map<string, string[]>()
        edges.filter(e => nodes.has(e.from) && nodes.has(e.to)).forEach(e => (predecessors.get(e.to) || predecessors.set(e.to, []).get(e.to)!).push(e.from))
        return new SimulationRun(secondsPerTick, sources, [...nodes.entries()].map(([key, node]) => ({ node, info: infos.get(key)! })), outputs, predecessors)
    }
}
